// Editor do corpo do e-mail (specs/25): negrito, itálico, sublinhado, título, listas, link, cor (paleta curta),
// imagem no corpo e variáveis. Sem dependência nova: contenteditable + execCommand. O valor é HTML; o que sai
// para a API passa pela mesma lista branca do servidor (sanitizarHtmlEmail), então colar do Word/Gmail não quebra o e-mail.
import {
  CORES_TEXTO_EMAIL, ehHtmlEmail, estilizarHtmlEmail, htmlParaTexto, IMAGENS_POR_EMAIL, renderizarEmail,
  sanitizarHtmlEmail, textoParaHtml, VARIAVEIS, type ContextoRender,
} from '@crm/contracts';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { api, mensagemErro } from '../api';
import { prepararImagem } from '../imagem';

/** Corpo antigo (texto puro) vira HTML do editor; HTML fica como está. */
export const paraHtmlEmail = (corpo: string | null | undefined) => (!corpo ? '' : ehHtmlEmail(corpo) ? corpo : textoParaHtml(corpo));
/** O que vai para a API: limpo pela lista branca. */
export const corpoEmailFinal = (html: string) => sanitizarHtmlEmail(html);
/** Vazio de verdade (só marcações, sem texto nem imagem)? */
export const corpoEmailVazio = (html: string) => !htmlParaTexto(html).trim() && !/<img\s/i.test(html);
/** HTML pronto para a prévia (variáveis do exemplo escapadas, estilos do e-mail). */
export function previaHtmlEmail(html: string, ctx: ContextoRender) {
  const r = renderizarEmail(sanitizarHtmlEmail(html), ctx);
  return { html: estilizarHtmlEmail(r.texto), faltando: r.faltando };
}

interface ImagemSalva { id: string; url: string }

/** Sobe a imagem para o CRM (fica no banco, entra embutida no envio). */
export async function subirImagemEmail(arquivo: File, largura?: number): Promise<ImagemSalva & { largura: number }> {
  const dataUrl = await prepararImagem(arquivo);
  const natural = await new Promise<number>((ok) => {
    const img = new Image();
    img.onload = () => ok(img.naturalWidth);
    img.onerror = () => ok(600);
    img.src = dataUrl;
  });
  const r = await api<ImagemSalva>('/email/imagens', { body: { dataUrl, ...(largura ? { largura } : {}) } });
  return { ...r, largura: Math.min(natural, largura ?? 600) };
}

type Pop = null | 'link' | 'cor' | 'var';

export function EditorEmail({ valor, onChange, disabled = false, minAltura = 220, rotulo = 'Texto do e-mail' }: {
  valor: string;
  onChange: (html: string) => void;
  disabled?: boolean;
  minAltura?: number;
  rotulo?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const ultimo = useRef<string | null>(null);
  const selecao = useRef<Range | null>(null);
  const [pop, setPop] = useState<Pop>(null);
  const [href, setHref] = useState('https://');
  const [erro, setErro] = useState<string | null>(null);
  const [subindo, setSubindo] = useState(false);
  const [ativos, setAtivos] = useState<Record<string, boolean>>({});

  // valor de fora (template escolhido, rascunho carregado) → DOM; o que o próprio editor emitiu não volta
  useEffect(() => {
    const el = ref.current;
    if (!el || valor === ultimo.current) return;
    el.innerHTML = paraHtmlEmail(valor);
    ultimo.current = valor;
  }, [valor]);

  const emitir = () => {
    const el = ref.current;
    if (!el) return;
    const html = el.innerHTML;
    ultimo.current = html;
    onChange(html);
  };

  const lerAtivos = () => {
    try {
      setAtivos({
        bold: document.queryCommandState('bold'), italic: document.queryCommandState('italic'),
        underline: document.queryCommandState('underline'),
        ul: document.queryCommandState('insertUnorderedList'), ol: document.queryCommandState('insertOrderedList'),
        h3: /^h3$/i.test(String(document.queryCommandValue('formatBlock'))),
      });
    } catch { /* navegador sem queryCommandState */ }
  };

  const guardarSelecao = () => {
    const s = window.getSelection();
    if (s && s.rangeCount && ref.current?.contains(s.anchorNode)) selecao.current = s.getRangeAt(0).cloneRange();
  };
  const restaurarSelecao = () => {
    const el = ref.current;
    if (!el) return;
    el.focus();
    const s = window.getSelection();
    if (selecao.current && s) { s.removeAllRanges(); s.addRange(selecao.current); }
  };

  const cmd = (nome: string, arg?: string) => {
    restaurarSelecao();
    document.execCommand(nome, false, arg);
    emitir();
    lerAtivos();
    guardarSelecao();
  };

  const inserirHtml = (html: string) => {
    restaurarSelecao();
    document.execCommand('insertHTML', false, html);
    emitir();
    guardarSelecao();
  };

  const contarImagens = () => ref.current?.querySelectorAll('img').length ?? 0;

  async function inserirImagens(arquivos: File[]) {
    setErro(null);
    const imgs = arquivos.filter((f) => f.type.startsWith('image/'));
    if (!imgs.length) return;
    if (contarImagens() + imgs.length > IMAGENS_POR_EMAIL) return setErro(`No máximo ${IMAGENS_POR_EMAIL} imagens por e-mail.`);
    setSubindo(true);
    try {
      for (const f of imgs) {
        const r = await subirImagemEmail(f);
        inserirHtml(`<img src="${r.url}" width="${r.largura}" alt=""><br>`);
      }
    } catch (e) {
      setErro(mensagemErro(e));
    } finally {
      setSubindo(false);
    }
  }

  const aplicarLink = () => {
    const url = href.trim();
    setPop(null);
    if (!/^(https?:\/\/|mailto:)\S+$/i.test(url) || url === 'https://') return setErro('Link inválido. Use um endereço começando com https:// (ou mailto:).');
    setErro(null);
    restaurarSelecao();
    const s = window.getSelection();
    if (s && s.isCollapsed) document.execCommand('insertHTML', false, `<a href="${url.replace(/"/g, '%22')}">${url.replace(/</g, '&lt;')}</a>&nbsp;`);
    else document.execCommand('createLink', false, url);
    emitir();
  };

  // botão da barra: não tira o foco do texto (senão a seleção se perde)
  const Tb = ({ titulo, on, onClick, children, estilo }: { titulo: string; on?: boolean; onClick: () => void; children: ReactNode; estilo?: React.CSSProperties }) => (
    <button type="button" className={`tb${on ? ' on' : ''}`} title={titulo} aria-label={titulo} aria-pressed={on ?? undefined} disabled={disabled}
      style={estilo} onMouseDown={(e) => e.preventDefault()} onClick={onClick}>{children}</button>
  );

  return (
    <div className="editor-email">
      <div className="barra" role="toolbar" aria-label="Formatação do e-mail">
        <Tb titulo="Negrito (Ctrl+B)" on={ativos.bold} onClick={() => cmd('bold')} estilo={{ fontWeight: 700 }}>N</Tb>
        <Tb titulo="Itálico (Ctrl+I)" on={ativos.italic} onClick={() => cmd('italic')} estilo={{ fontStyle: 'italic' }}>I</Tb>
        <Tb titulo="Sublinhado (Ctrl+U)" on={ativos.underline} onClick={() => cmd('underline')} estilo={{ textDecoration: 'underline' }}>S</Tb>
        <span className="sep" aria-hidden="true" />
        <Tb titulo="Título" on={ativos.h3} onClick={() => cmd('formatBlock', ativos.h3 ? '<p>' : '<h3>')}>Título</Tb>
        <Tb titulo="Lista com marcadores" on={ativos.ul} onClick={() => cmd('insertUnorderedList')}>• Lista</Tb>
        <Tb titulo="Lista numerada" on={ativos.ol} onClick={() => cmd('insertOrderedList')}>1. Lista</Tb>
        <span className="sep" aria-hidden="true" />
        <span className="pop-ancora">
          <Tb titulo="Link" on={pop === 'link'} onClick={() => { guardarSelecao(); setHref('https://'); setPop(pop === 'link' ? null : 'link'); }}>Link</Tb>
          {pop === 'link' && (
            <span className="pop" role="dialog" aria-label="Endereço do link">
              <input className="input" autoFocus value={href} onChange={(e) => setHref(e.target.value)} style={{ height: 32, width: 260 }}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); aplicarLink(); } if (e.key === 'Escape') setPop(null); }} aria-label="Endereço do link" />
              <button type="button" className="btn sm primary" onClick={aplicarLink}>Aplicar</button>
              <button type="button" className="btn sm" onClick={() => { setPop(null); cmd('unlink'); }}>Tirar link</button>
            </span>
          )}
        </span>
        <span className="pop-ancora">
          <Tb titulo="Cor do texto" on={pop === 'cor'} onClick={() => { guardarSelecao(); setPop(pop === 'cor' ? null : 'cor'); }}>
            <span style={{ borderBottom: '3px solid #2563eb', lineHeight: 1 }}>A</span>
          </Tb>
          {pop === 'cor' && (
            <span className="pop" role="dialog" aria-label="Cor do texto">
              {CORES_TEXTO_EMAIL.map((c) => (
                <button key={c.cor} type="button" className="cor-btn" title={c.nome} aria-label={c.nome} style={{ background: c.cor }}
                  onMouseDown={(e) => e.preventDefault()} onClick={() => { setPop(null); cmd('foreColor', c.cor); }} />
              ))}
            </span>
          )}
        </span>
        <Tb titulo="Limpar formatação" onClick={() => { cmd('removeFormat'); cmd('formatBlock', '<p>'); }}>Limpar</Tb>
        <span className="sep" aria-hidden="true" />
        <label className={`tb${disabled || subindo ? ' off' : ''}`} title="Inserir imagem" onMouseDown={guardarSelecao}>
          {subindo ? 'Enviando…' : 'Imagem'}
          <input type="file" accept="image/png,image/jpeg,image/webp" hidden disabled={disabled || subindo}
            onChange={(e) => { const f = Array.from(e.target.files ?? []); e.target.value = ''; void inserirImagens(f); }} />
        </label>
        <span className="pop-ancora">
          <Tb titulo="Inserir variável" on={pop === 'var'} onClick={() => { guardarSelecao(); setPop(pop === 'var' ? null : 'var'); }}>{'{ }'} Variável</Tb>
          {pop === 'var' && (
            <span className="pop col" role="menu" aria-label="Variáveis" style={{ alignItems: 'stretch', gap: 2 }}>
              {VARIAVEIS.map((v) => (
                <button key={v} type="button" role="menuitem" className="var-btn" style={{ textAlign: 'left' }}
                  onMouseDown={(e) => e.preventDefault()} onClick={() => { setPop(null); restaurarSelecao(); document.execCommand('insertText', false, `{{${v}}}`); emitir(); }}>
                  {`{{${v}}}`}
                </button>
              ))}
            </span>
          )}
        </span>
      </div>
      <div
        ref={ref}
        className="editor"
        role="textbox"
        aria-multiline="true"
        aria-label={rotulo}
        contentEditable={!disabled}
        suppressContentEditableWarning
        style={{ minHeight: minAltura }}
        onFocus={() => { try { document.execCommand('defaultParagraphSeparator', false, 'p'); } catch { /* ok */ } }}
        onInput={emitir}
        onKeyUp={() => { lerAtivos(); guardarSelecao(); }}
        onMouseUp={() => { lerAtivos(); guardarSelecao(); }}
        onBlur={guardarSelecao}
        onPaste={(e) => {
          const arquivos = Array.from(e.clipboardData.files ?? []).filter((f) => f.type.startsWith('image/'));
          if (arquivos.length) { e.preventDefault(); guardarSelecao(); void inserirImagens(arquivos); return; }
          const html = e.clipboardData.getData('text/html');
          e.preventDefault();
          if (html) document.execCommand('insertHTML', false, sanitizarHtmlEmail(html) || ' ');
          else document.execCommand('insertText', false, e.clipboardData.getData('text/plain'));
          emitir();
        }}
        onDrop={(e) => {
          const arquivos = Array.from(e.dataTransfer.files ?? []).filter((f) => f.type.startsWith('image/'));
          if (!arquivos.length) return;
          e.preventDefault();
          guardarSelecao();
          void inserirImagens(arquivos);
        }}
      />
      {erro && <span className="small" role="alert" style={{ color: 'var(--red-text)' }}>{erro}</span>}
    </div>
  );
}

/** Prévia do e-mail como o cliente recebe (HTML já limpo). */
export function PreviaEmailHtml({ html, ctx, assunto, children }: { html: string; ctx: ContextoRender; assunto?: string; children?: ReactNode }) {
  const p = previaHtmlEmail(html, ctx);
  return (
    <div className="previa-email html">
      {assunto !== undefined && <div className="assunto">{assunto || '(sem assunto)'}</div>}
      <div className="corpo-html" dangerouslySetInnerHTML={{ __html: p.html || '<p style="color:#8a8f98">—</p>' }} />
      {children}
    </div>
  );
}

/** Assinatura na prévia: imagem ao lado ou acima do texto (igual ao envio). */
export function PreviaAssinatura({ texto, imagemUrl, largura = 180, posicao = 'LADO' }: {
  texto?: string | null; imagemUrl?: string | null; largura?: number; posicao?: 'LADO' | 'ACIMA';
}) {
  if (!texto && !imagemUrl) return null;
  return (
    <div className="assinatura" style={{ display: 'flex', gap: 14, ...(posicao === 'LADO' ? { alignItems: 'center' } : { flexDirection: 'column', alignItems: 'flex-start' }) }}>
      {imagemUrl && <img src={imagemUrl} width={largura} alt="" style={{ display: 'block', height: 'auto', maxWidth: '100%' }} />}
      {texto && <div style={{ whiteSpace: 'pre-line' }}>{texto}</div>}
    </div>
  );
}
